import { supabase } from '../lib/supabase';
import { validateSchemaStrict } from '../utils/schemaValidator';

export const supplierService = {
  // Get all suppliers
  async getAllSuppliers() {
    try {
      const { data, error } = await supabase?.from('suppliers')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supplier profile by user ID (includes addresses). If missing, attempt to create via RPC helper.
  async getSupplierByUserId(userId) {
    try {
      // Try to get supplier by user id
      const { data: supplier, error: supplierError } = await supabase?.from('suppliers')?.select('*')?.eq('user_id', userId)?.single();

      let finalSupplier = supplier;

      // If supplier missing, try RPC helper to ensure creation (migration must be applied)
      if (!finalSupplier) {
        try {
          const rpcRes = await supabase?.rpc('ensure_supplier_for_user', { p_user_id: userId });
          if (rpcRes?.data) {
            const supplierId = rpcRes?.data || rpcRes;
            const { data: s } = await supabase?.from('suppliers')?.select('*')?.eq('id', supplierId)?.single();
            finalSupplier = s;
          }
        } catch (rpcErr) {
          // Ignore RPC error; we'll surface missing supplier to caller
          console.warn('ensure_supplier_for_user RPC failed:', rpcErr?.message || rpcErr);
        }
      }

      if (!finalSupplier) {
        return { data: null, error: new Error('Supplier not found') };
      }

      // Fetch addresses separately (avoid relying on PostgREST relationship cache) using user_id on addresses
      try {
        const { data: addresses } = await supabase?.from('addresses')?.select('*')?.eq('user_id', userId)?.order('is_primary', { ascending: false });
        finalSupplier = { ...finalSupplier, addresses: addresses || [] };
      } catch (addrErr) {
        // Non-fatal - attach empty addresses
        finalSupplier = { ...finalSupplier, addresses: [] };
      }

      return { data: finalSupplier, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supplier profile by supplier ID
  async getSupplierById(supplierId) {
    try {
      // Fetch supplier
      const { data: supplier, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('id', supplierId)
        .single();
      if (error) throw error;
      if (!supplier) return { data: null, error: new Error('Supplier not found') };

      // Fetch addresses by user_id
      let addresses = [];
      if (supplier.user_id) {
        const { data: addrData } = await supabase
          .from('addresses')
          .select('*')
          .eq('user_id', supplier.user_id)
          .order('is_primary', { ascending: false });
        addresses = addrData || [];
      }

      // Fetch user profile for phone/email
      let user = null;
      if (supplier.user_id) {
        const { data: userData } = await supabase
          .from('user_profiles')
          .select('id, email, phone')
          .eq('id', supplier.user_id)
          .single();
        user = userData || null;
      }

      return { data: { ...supplier, addresses, user }, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supplier menu items
  async getMenuItems(supplierId) {
    try {
      const { data, error } = await supabase?.from('menu_items')?.select('*')?.eq('supplier_id', supplierId)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supplier orders
  async getOrders(supplierId) {
    try {
      const { data, error } = await supabase?.from('orders').select(`
          *,
          customer:customers!orders_customer_id_fkey(
            id,
            user:user_profiles!customers_user_id_fkey(
              id,
              full_name,
              avatar_url
            )
          ),
          order_items(
            *,
            menu_item:menu_items(
              name,
              price_per_plate,
              image
            )
          )
        `).eq('supplier_id', supplierId).order('created_at', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get supplier notifications
  async getNotifications(userId) {
    try {
      const { data, error } = await supabase?.from('notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false })?.limit(10);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update order status
  async updateOrderStatus(orderId, status) {
    try {
      const { data, error } = await supabase?.from('orders')?.update({ status })?.eq('id', orderId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get revenue data (last 6 months)
  async getRevenueData(supplierId) {
    try {
      const sixMonthsAgo = new Date();
      sixMonthsAgo?.setMonth(sixMonthsAgo?.getMonth() - 6);

      const { data, error } = await supabase?.from('orders')?.select('total_amount, created_at')?.eq('supplier_id', supplierId)?.gte('created_at', sixMonthsAgo?.toISOString())?.order('created_at', { ascending: true });

      if (error) throw error;

      // Group by month
      const monthlyData = {};
      data?.forEach((order) => {
        const month = new Date(order.created_at)?.toLocaleString('en-US', { month: 'short' });
        if (!monthlyData?.[month]) {
          monthlyData[month] = { revenue: 0, orders: 0 };
        }
        monthlyData[month].revenue += parseFloat(order?.total_amount);
        monthlyData[month].orders += 1;
      });

      const revenueData = Object.entries(monthlyData)?.map(([month, data]) => ({
        month,
        revenue: data?.revenue,
        orders: data?.orders
      }));

      return { data: revenueData, error: null };
    } catch (error) {
      return { data: [], error };
    }
  },

  // Create new menu item
  async createMenuItem(menuItemData) {
    try {
      // Validate schema before database operation
      const validatedData = await validateSchemaStrict('menu_items', menuItemData);
      
      const { data, error } = await supabase?.from('menu_items')?.insert([validatedData])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update supplier profile
  async updateSupplier(supplierId, updates) {
    try {
      // Only allow columns that exist in the suppliers table
      const allowed = ['business_name', 'service_description', 'country', 'profile_image', 'currency'];
      const dbUpdates = {};
      for (const key of allowed) {
        if (key in updates) dbUpdates[key] = updates[key];
      }
      const { data, error } = await supabase?.from('suppliers')?.update(dbUpdates)?.eq('id', supplierId)?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Upsert an address for supplier
  async upsertSupplierAddress(supplierId, address) {
    try {
      const payload = {
        id: address?.id || undefined,
        label: address?.label || 'Work',
        country: address?.country || null,
        state: address?.state || null,
        city: address?.city || null,
        locality: address?.locality ?? address?.street ?? null,
        pin_code: address?.pinCode ?? address?.pin_code ?? address?.zipCode ?? null,
        is_primary: address?.is_primary ?? address?.isPrimary ?? false
      };
      // Only set user_id if present and valid
      if (address?.user_id) {
        payload.user_id = address.user_id;
      }

      // If id present, update; else insert
      if (payload.id) {
        const { data, error } = await supabase?.from('addresses')?.update(payload)?.eq('id', payload.id)?.select()?.single();
        if (error) throw error;
        return { data, error: null };
      }

      const { data, error } = await supabase?.from('addresses')?.insert([payload])?.select()?.single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update supplier addresses array (create/update/delete to match provided array)
  async updateSupplierAddresses(supplierId, addresses) {
    try {
      if (!supplierId) throw new Error('Supplier ID is required');

      // Fetch existing addresses for user
      const { data: existing = [], error: existingErr } = await supabase?.from('addresses')?.select('id')?.eq('user_id', addresses?.[0]?.user_id);
      if (existingErr) throw existingErr;

      const existingIds = existing?.map(e => e?.id) || [];
      const incoming = Array.isArray(addresses) ? addresses : [];

      const toCreate = incoming.filter(a => !a?.id);
      const toUpdate = incoming.filter(a => a?.id);
      const incomingIds = incoming.filter(a => a?.id).map(a => a.id);
      const toDelete = existingIds.filter(id => !incomingIds.includes(id));

      // Create new addresses
      if (toCreate?.length > 0) {
        const payloads = toCreate.map((a) => {
          const payload = {
            label: a?.label ?? null,
            country: a?.country ?? null,
            state: a?.state ?? null,
            city: a?.city ?? null,
            locality: a?.locality ?? a?.street ?? null,
            pin_code: a?.pinCode ?? a?.pin_code ?? a?.zipCode ?? null,
            is_primary: a?.isPrimary ?? a?.is_primary ?? false
          };
          if (a?.user_id) payload.user_id = a.user_id;
          return payload;
        });

        const { data: created, error: createErr } = await supabase?.from('addresses')?.insert(payloads)?.select();
        if (createErr) throw createErr;
      }

      // Update existing addresses
      for (const a of toUpdate) {
        const payload = {
          label: a?.label,
          country: a?.country,
          state: a?.state,
          city: a?.city,
          locality: a?.locality ?? a?.street,
          pin_code: a?.pinCode ?? a?.pin_code ?? a?.zipCode,
          is_primary: a?.isPrimary ?? a?.is_primary
        };
        const { data: updated, error: updErr } = await supabase?.from('addresses')?.update(payload)?.eq('id', a?.id)?.select()?.single();
        if (updErr) throw updErr;
      }

      // Delete removed addresses
      if (toDelete?.length > 0) {
        const { error: delErr } = await supabase?.from('addresses')?.delete()?.in('id', toDelete);
        if (delErr) throw delErr;
      }

      // Return refreshed list
      const { data: refreshed, error: refErr } = await supabase?.from('addresses')?.select('*')?.eq('supplier_id', supplierId)?.order('is_primary', { ascending: false });
      if (refErr) throw refErr;

      return { data: refreshed, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update menu item
  async updateMenuItem(itemId, updates) {
    try {
      // Validate schema before database operation
      const validatedData = await validateSchemaStrict('menu_items', updates);
      
      const { data, error } = await supabase?.from('menu_items')?.update(validatedData)?.eq('id', itemId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Delete menu item
  async deleteMenuItem(itemId) {
    try {
      const { data, error } = await supabase?.from('menu_items')?.delete()?.eq('id', itemId);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get unread message count
  async getUnreadCount(userId) {
    try {
      const { count: data, error } = await supabase?.from('messages')?.select('id', { count: 'exact', head: true })?.eq('is_read', false)?.neq('sender_id', userId);

      if (error) throw error;
      return { count: data || 0, error: null };
    } catch (error) {
      return { count: 0, error };
    }
  },

  // Subscribe to new orders for supplier (real-time)
  subscribeToNewOrders(supplierId, callback) {
    const channel = supabase?.channel(`orders:supplier:${supplierId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `supplier_id=eq.${supplierId}`
        },
        async (payload) => {
          // Fetch complete order with customer and items info
          const { data } = await supabase?.from('orders')?.select(`
              *,
              customer:customers!orders_customer_id_fkey(
                id,
                user:user_profiles!customers_user_id_fkey(
                  full_name,
                  avatar_url,
                  phone
                )
              ),
              order_items(
                *,
                menu_item:menu_items(
                  name,
                  price_per_plate
                )
              )
            `)
            ?.eq('id', payload?.new?.id)
            ?.single();

          if (data) callback(data);
        }
      )
      ?.subscribe();

    return channel;
  }
};